import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { motion } from "framer-motion"
import axiosInstance from "../../utils/axios/axiosConfig"
import { toast } from "sonner"
import { Loading } from "../../components"
import { TextInput, TextArea, RichEditor } from "../../components"
import { Camera, Upload, Trash2 } from "lucide-react"

function FormBlogs() {
  const navigate = useNavigate()
  const { id } = useParams()

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [editorKey, setEditorKey] = useState(0)

  const [formData, setFormData] = useState({
    image: null, // base64 or existing url (edit)
    title: "",
    slug: "",
    description: "",
    meta_title: "",
    meta_description: "",
    content: "",
  })

  // -----------------------------
  // Helpers
  // -----------------------------
  const unwrapChange = (v) => {
    if (v && typeof v === "object" && (v.target || v.currentTarget)) {
      return v.target?.value ?? v.currentTarget?.value ?? ""
    }
    if (v && typeof v === "object" && "value" in v) return v.value
    return v
  }

  const toText = (v) => {
    if (v == null) return ""
    if (typeof v === "string" || typeof v === "number") return String(v)

    if (typeof v === "object") {
      for (const k of ["en", "fr", "ar", "value", "title", "name"]) {
        if (v?.[k] != null && (typeof v[k] === "string" || typeof v[k] === "number")) {
          return String(v[k])
        }
      }
      const first = Object.values(v).find((x) => typeof x === "string" || typeof x === "number")
      return first != null ? String(first) : ""
    }

    return ""
  }

  const setField = (name, raw) => {
    let value = unwrapChange(raw)

    if (["title", "slug", "description", "meta_title", "meta_description", "content"].includes(name)) {
      value = toText(value)
    }

    setFormData((prev) => ({ ...prev, [name]: value }))

    setErrors((prev) => {
      if (!prev[name]) return prev
      const copy = { ...prev }
      delete copy[name]
      return copy
    })
  }

  const isBase64Image = (v) =>
    typeof v === "string" && v.startsWith("data:image/") && v.includes(";base64,")

  const normalizeBlog = (blog) => {
    if (!blog) return null
    return {
      image: blog.image ?? null,
      title: toText(blog.title),
      slug: toText(blog.slug),
      description: toText(blog.description),
      meta_title: toText(blog.meta_title),
      meta_description: toText(blog.meta_description),
      content: toText(blog.content ?? ""),
    }
  }

  const buildPayload = () => {
    const payload = {
      title: toText(formData.title),
      slug: toText(formData.slug),
      description: toText(formData.description),
      meta_title: toText(formData.meta_title),
      meta_description: toText(formData.meta_description),
      content: toText(formData.content),
    }

    // Only send image when:
    // - null => user removed it
    // - base64 => user uploaded a new one
    // If it's an existing URL during edit, omit it to keep current image.
    if (formData.image === null) payload.image = null
    else if (isBase64Image(formData.image)) payload.image = formData.image

    return payload
  }

  const validateForm = () => {
    const e = {}
    if (!toText(formData.title).trim()) e.title = "Title is required"
    if (!toText(formData.slug).trim()) e.slug = "Slug is required"
    if (!toText(formData.content).trim()) e.content = "Content is required"
    setErrors(e)
    return Object.keys(e).length === 0
  }

  // -----------------------------
  // Data Fetch
  // -----------------------------
  useEffect(() => {
    if (id) getBlogToUpdate()
    else {
      // fresh create
      setFormData({
        image: null,
        title: "",
        slug: "",
        description: "",
        meta_title: "",
        meta_description: "",
        content: "",
      })
      setEditorKey((k) => k + 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const getBlogToUpdate = async () => {
    try {
      setLoading(true)
      const res = await axiosInstance.get(`/blogs/get/${id}`)
      const blog = res?.data?.data
      const normalized = normalizeBlog(blog)

      if (normalized) {
        setFormData((prev) => ({ ...prev, ...normalized }))
        // ✅ Force RichEditor to show fetched content
        setEditorKey((k) => k + 1)
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch blog")
    } finally {
      setLoading(false)
    }
  }

  // -----------------------------
  // Image
  // -----------------------------
  const handleImageChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 15 * 1024 * 1024) {
      toast.warning("File size exceeds 15 MB limit")
      e.target.value = ""
      return
    }

    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => setField("image", reader.result)
  }

  const removePhoto = () => setField("image", null)

  // -----------------------------
  // Submit
  // -----------------------------
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    try {
      setLoading(true)
      const payload = buildPayload()

      const res = id
        ? await axiosInstance.put(`/blogs/update/${id}`, payload)
        : await axiosInstance.post(`/blogs/create`, payload)

      toast.success(res?.data?.message || "Saved")
      navigate("/blogs")
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occurred while saving")
    } finally {
      setLoading(false)
    }
  }

  // -----------------------------
  // UI (same design)
  // -----------------------------
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -18 }}
      transition={{ duration: 0.25 }}
      className="w-full h-full px-6 py-5 bg-neutral-50/80"
    >
      {loading && <Loading />}

      <div className="flex flex-col items-start gap-1 mb-6">
        <h1 className="text-xl font-semibold text-neutral-900 dark:text-white">Post</h1>
        <p className="text-sm text-neutral-600">Add or update post here.</p>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-neutral-200 p-6">
            <div className="space-y-5">
              <TextInput
                label="Title"
                name="title"
                value={toText(formData.title)}
                onChange={(v) => setField("title", v)}
                placeholder="Enter post title"
                error={errors.title}
              />

              <TextInput
                label="Slug"
                name="slug"
                value={toText(formData.slug)}
                onChange={(v) => setField("slug", v)}
                placeholder="enter-post-slug"
                error={errors.slug}
              />

              <div>
                <label className="block text-sm mb-2 text-start font-medium text-neutral-700">
                  Content
                </label>

                <RichEditor
                  key={`${id || "new"}-${editorKey}`}
                  value={toText(formData.content)}
                  onChange={(v) => setField("content", v)}
                />

                {errors.content && <p className="text-sm text-red-500 mt-2">{errors.content}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Right */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-neutral-200 p-6">
            <div className="space-y-6">
              {/* Image */}
              <div>
                <label className="block text-sm mb-2 text-start font-medium text-neutral-700">
                  Featured Image
                </label>
                <div className="mt-2 border-2 border-dashed border-neutral-200 rounded-2xl p-6 text-center">
                  {formData.image ? (
                    <div className="relative">
                      <img
                        src={formData.image}
                        alt="Preview"
                        className="max-h-[200px] mx-auto object-contain rounded-xl"
                      />
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Camera className="mx-auto h-12 w-12 text-neutral-400" />
                      <div className="mt-4">
                        <label
                          htmlFor="image-upload"
                          className="cursor-pointer inline-flex items-center px-4 py-2 border border-neutral-300 rounded-xl shadow-sm text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50"
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          Upload Image
                        </label>
                        <input
                          id="image-upload"
                          type="file"
                          className="hidden"
                          onChange={handleImageChange}
                          accept="image/*"
                        />
                      </div>
                      <p className="text-xs text-neutral-500 mt-2">JPG, GIF or PNG. Max size 15MB</p>
                    </div>
                  )}
                </div>
              </div>

              <TextInput
                label="Meta Title"
                name="meta_title"
                value={toText(formData.meta_title)}
                onChange={(v) => setField("meta_title", v)}
                placeholder="Optional"
              />

              <TextArea
                label="Meta Description"
                name="meta_description"
                value={toText(formData.meta_description)}
                onChange={(v) => setField("meta_description", v)}
                rows={3}
                placeholder="Optional"
              />

              <TextArea
                label="Description"
                name="description"
                value={toText(formData.description)}
                onChange={(v) => setField("description", v)}
                rows={3}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/blogs")}
              className="px-4 py-2 border border-neutral-300 rounded-xl shadow-sm text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-[#2B2B35] rounded-xl hover:bg-cyan-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? (id ? "Updating..." : "Creating...") : id ? "Update Post" : "Create Post"}
            </button>
          </div>
        </div>
      </form>
    </motion.div>
  )
}

export default FormBlogs
